import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  IconButton, 
  Chip,
  Divider,
  Button,
  LinearProgress,
  Tooltip
} from '@mui/joy';
import {
  CurrencyDollar as DollarSign,
  Calendar,
  User,
  PencilSimple as Edit2,
  Trash as Trash2,
  Eye,
  Target,
  TrendUp as TrendingUp,
  Clock
} from '@phosphor-icons/react';
import type { Opportunity } from '../../types';
import { userDisplayName } from '../../utils/userDisplay';

interface OpportunityCardProps {
  opportunity: Opportunity;
  onEdit?: (opportunity: Opportunity) => void;
  onDelete?: (opportunityId: number) => void;
}

export const OpportunityCard: React.FC<OpportunityCardProps> = ({ 
  opportunity, 
  onEdit, 
  onDelete 
}) => {
  const navigate = useNavigate();
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: opportunity.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent drag events
    navigate(`/crm/opportunities/${opportunity.id}`);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'low':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'won':
        return 'success';
      case 'lost':
        return 'danger';
      case 'open':
        return 'primary';
      default:
        return 'neutral';
    }
  };

  const isOverdue = opportunity.expected_close_date && 
    new Date(opportunity.expected_close_date) < new Date() && 
    opportunity.status === 'open';

  // Primary rep first, then additional reps.
  const reps = [
    ...(opportunity.assigned_to ? [opportunity.assigned_to] : []),
    ...(opportunity.additional_reps || []),
  ];

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      variant="outlined"
      sx={{
        cursor: 'move',
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.2s ease',
        borderLeft: `4px solid`,
        borderLeftColor: isOverdue ? 'danger.500' : 'primary.300',
        flexShrink: 0,
        backgroundColor: opportunity.status === 'lost' ? 'danger.softBg'
                      : opportunity.status === 'won' ? 'success.softBg'
                      : 'background.surface',
        '&:hover': {
          boxShadow: 'md',
        },
        '&:hover .card-actions': {
          opacity: 1,
        },
      }}
    >
      <CardContent sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
          <Box sx={{ flex: 1 }}>
            <Typography level="title-sm" sx={{ lineHeight: 1.2, mb: 0.25 }}>
              {opportunity.name}
            </Typography>
            {(opportunity.contact_details || opportunity.contact_name) && (
              <Typography level="body-xs" textColor="text.primary" fontWeight="md">
                {opportunity.contact_details 
                  ? `${opportunity.contact_details.first_name} ${opportunity.contact_details.last_name}`
                  : opportunity.contact_name}
              </Typography>
            )}
          </Box>
          <Box 
            className="card-actions"
            sx={{ 
              display: 'flex', 
              gap: 0.5, 
              opacity: 0, 
              transition: 'opacity 0.2s ease' 
            }}
          >
            {onEdit && (
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(opportunity);
                }}
              >
                <Edit2 size={12} />
              </IconButton>
            )}
            {onDelete && (
              <IconButton
                size="sm"
                variant="plain"
                color="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(opportunity.id);
                }}
              >
                <Trash2 size={12} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Value and Probability */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DollarSign size={12} />
              <Typography level="body-xs" fontWeight="lg" textColor="text.primary">
                {formatCurrency(parseFloat(String(opportunity.estimated_value || 0)) || 0)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Target size={12} />
              <Typography level="body-xs" textColor="text.secondary">
                {opportunity.probability}%
              </Typography>
            </Box>
          </Box>
          
          {/* Probability bar */}
          <LinearProgress
            determinate
            value={opportunity.probability}
            size="sm"
            color={opportunity.probability > 70 ? 'success' : opportunity.probability > 40 ? 'warning' : 'neutral'}
          />
        </Box>

        {/* Source Tag only */}
        {opportunity.source && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
            <Chip size="sm" variant="outlined" color="neutral">
              {opportunity.source}
            </Chip>
          </Box>
        )}

        {/* Key Information */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {opportunity.expected_close_date && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Calendar size={12} />
              <Typography 
                level="body-xs" 
                textColor={isOverdue ? 'danger.500' : 'text.secondary'}
                fontWeight={isOverdue ? 'lg' : 'normal'}
              >
                {isOverdue && <Clock size={10} style={{ marginRight: 2 }} />}
                Close: {formatDate(opportunity.expected_close_date)}
              </Typography>
            </Box>
          )}

          {reps.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <User size={12} />
              <Typography level="body-xs" textColor="text.secondary" noWrap>
                {userDisplayName(reps[0])}
              </Typography>
              {reps.length > 1 && (
                <Tooltip title={reps.slice(1).map(userDisplayName).join(', ')} size="sm" variant="soft">
                  <Chip
                    size="sm"
                    variant="soft"
                    color="primary"
                    aria-label={`${reps.length - 1} more reps: ${reps.slice(1).map(userDisplayName).join(', ')}`}
                    sx={{ '--Chip-minHeight': '18px', fontSize: '0.65rem', px: 0.75 }}
                  >
                    +{reps.length - 1}
                  </Chip>
                </Tooltip>
              )}
            </Box>
          )}

          {opportunity.next_action && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUp size={12} />
              <Typography level="body-xs" textColor="text.secondary">
                Next: {opportunity.next_action}
              </Typography>
            </Box>
          )}
        </Box>



        <Divider sx={{ my: 0.75 }} />
        <Button
          size="sm"
          variant="outlined"
          color="primary"
          startDecorator={<Eye size={14} />}
          onClick={handleViewDetails}
          sx={{ width: '100%' }}
        >
          View Details
        </Button>
      </CardContent>
    </Card>
  );
};